export type InvitePreview = {
  id: string;
  email: string;
  status: string;
  organization_name?: string | null;
  invited_by_name?: string | null;
};

export type InviteLoadState = 'idle' | 'loading' | 'loaded' | 'not_found' | 'error';

export type InviteLanding = {
  /** Whether the email form should render the signup fields rather than sign-in. */
  isSignUp: boolean;
  heading: string;
  subheading: string;
  /** Inline banner copy, or null when there is nothing extra to say. */
  notice: string | null;
  noticeTone: 'info' | 'warning';
  /** Email to prefill from the invite, when we know it. */
  prefillEmail: string | null;
};

const ORG_FALLBACK = 'your team';

function orgLabel(preview: InvitePreview | null): string {
  const name = preview?.organization_name?.trim();
  return name || ORG_FALLBACK;
}

/**
 * Copy + form mode for /login?invite_id=... . An invitee should land on a signup
 * state that names the org they are joining, never on a bare sign-in form they
 * have to figure out (AIM-407). Invites that are already accepted are the one
 * case where sign-in is the right destination.
 */
export function resolveInviteLanding(
  preview: InvitePreview | null,
  state: InviteLoadState,
): InviteLanding {
  if (state === 'idle') {
    return {
      isSignUp: false,
      heading: 'Welcome back',
      subheading: 'Sign in to your account',
      notice: null,
      noticeTone: 'info',
      prefillEmail: null,
    };
  }

  if (state === 'not_found') {
    return {
      isSignUp: false,
      heading: 'Welcome back',
      subheading: 'Sign in to your account',
      notice: "We couldn't find that invitation. It may have been revoked or already used. Sign in below, or ask your admin to resend it.",
      noticeTone: 'warning',
      prefillEmail: null,
    };
  }

  // Loading, or the lookup failed for a reason other than a missing invite: we
  // still know an invite id was present, so keep the invite-aware signup state.
  if (state === 'loading' || state === 'error') {
    return {
      isSignUp: true,
      heading: "You've been invited to Aimdoc",
      subheading: 'Create your account to join your team',
      notice: null,
      noticeTone: 'info',
      prefillEmail: null,
    };
  }

  const org = orgLabel(preview);
  const inviter = preview?.invited_by_name?.trim();

  if (preview?.status === 'accepted') {
    return {
      isSignUp: false,
      heading: 'Welcome back',
      subheading: `Sign in to continue to ${org}`,
      notice: `This invitation has already been accepted. Sign in to continue to ${org}.`,
      noticeTone: 'info',
      prefillEmail: preview?.email || null,
    };
  }

  return {
    isSignUp: true,
    heading: `Join ${org} on Aimdoc`,
    subheading: inviter
      ? `${inviter} invited you to join ${org}. Create your account to get started.`
      : `You've been invited to join ${org}. Create your account to get started.`,
    notice: null,
    noticeTone: 'info',
    prefillEmail: preview?.email || null,
  };
}
